/**
 * Calendar Event Types
 */

// Event categories
export type EventCategory = 'quality' | 'commercial' | 'recruitment' | 'internal' | 'follow_up' | 'other';

// Type for event types structure from API (object with metadata)
export type EventTypesStructure = Record<string, Record<string, { user_required: boolean; company_required: boolean; worker_required: boolean }>>;

// Get category from type using API types structure
export function getCategoryFromType(type: string, typesSource: EventTypesStructure): EventCategory {
  for (const [category, types] of Object.entries(typesSource)) {
    if (type in types) {
      return category as EventCategory;
    }
  }
  return 'other';
}

// All event types as flat array from API types structure
export function getAllEventTypes(typesSource: EventTypesStructure): string[] {
  return Object.values(typesSource).flatMap((types) => Object.keys(types));
}

// User in event
export interface EventUser {
  id: number;
  first_name: string;
  last_name: string;
  email?: string;
}

// Worker in event
export interface EventWorker {
  id: number;
  first_name: string;
  last_name: string;
}

// Company in event
export interface EventCompany {
  id: number;
  name: string;
}

// Contact in event
export interface EventContact {
  id: number;
  first_name: string;
  last_name: string;
  company_id?: number;
  company_name?: string;
}

// Deal summary linked to an event (API enrichment)
export interface EventDeal {
  id: number;
  title: string;
  company_id?: number | null;
  stage_id?: number | null;
}

// Notification
export interface EventNotification {
  id: number;
  calendar_event_id: number;
  minutes_before: number;
  notification_sent_at: string | null;
}

// Calendar Event
export interface CalendarEvent {
  id: number;
  user_id: number;
  user_first_name?: string;
  user_last_name?: string;
  type: string;
  title: string;
  description: string | null;
  address: string | null;
  address_position: { lat: number; lng: number } | null;
  result: string | null;
  start_date: string;
  end_date: string;
  is_private: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // Relations
  users: EventUser[];
  workers: EventWorker[];
  companies: EventCompany[];
  contacts: EventContact[];
  notification: EventNotification | null;
  deal_id?: number | null;
  deal?: EventDeal | null;
  // Computed (always provided by API)
  category: EventCategory;
  // External calendar (Google/Microsoft) - only for list/display
  source?: 'google' | 'microsoft';
  url?: string | null;
}

// Event form data (for create/update)
export interface CalendarEventFormData {
  title: string;
  type: string;
  description: string;
  address: string;
  result: string;
  is_private: boolean;
  agency_id?: number | null;
  user_ids: number[];
  worker_ids: number[];
  company_ids: number[];
  contact_ids: number[];
  deal_id: number | null;
  notification_minutes_before: number | null;
  address_position?: { latitude: number; longitude: number } | null;
}

// Filters for event list
export interface CalendarEventFilters {
  space_ids?: number[];
  user_ids?: number[];
  categories?: EventCategory[];
  types?: string[];
  from?: string;
  to?: string;
  company_id?: number;
  worker_id?: number;
}

// API response for list
export interface CalendarEventListResponse {
  data: CalendarEvent[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// Default category colors
export const DEFAULT_CATEGORY_COLORS: Record<EventCategory, string> = {
  quality: '#4CAF50',      // Green
  commercial: '#2196F3',   // Blue
  recruitment: '#FF9800',  // Orange
  internal: '#9C27B0',     // Purple
  follow_up: '#E91E63',    // Pink
  other: '#607D8B',        // Blue Grey
};

// Get user's category colors from localStorage
export function getCategoryColors(): Record<EventCategory, string> {
  const stored = localStorage.getItem('calendar_category_colors');
  if (stored) {
    try {
      return { ...DEFAULT_CATEGORY_COLORS, ...JSON.parse(stored) };
    } catch {
      return DEFAULT_CATEGORY_COLORS;
    }
  }
  return DEFAULT_CATEGORY_COLORS;
}

// Save category colors to localStorage
export function saveCategoryColors(colors: Record<EventCategory, string>): void {
  localStorage.setItem('calendar_category_colors', JSON.stringify(colors));
}

// Get color for event based on its category (category is always provided by API)
export function getEventColor(event: CalendarEvent): string {
  const colors = getCategoryColors();
  return colors[event.category];
}
